import { isEqual } from 'lodash';
import { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Breadcrumbs, type BreadcrumbsItemType } from '@air/component-breadcrumbs';

import { BOARD_CRUMBS } from '~/constants/testIDs';
import { usePublicBoard } from '~/providers/PublicBoardProvider';
import {
  CentralizedBoardSelectors,
  centralizedBoardAncestorsSelector,
  centralizedBoardTitleSelector,
} from '~/store/centralizedBoard/selectors';
import { type LocationBoard } from '~/store/router/types';

const COMPACT_BREADCRUMBS_THRESHOLD = 4;

export const PublicBoardHeaderAncestors = memo(() => {
  const ancestors = useSelector(centralizedBoardAncestorsSelector, isEqual);
  const id = useSelector(CentralizedBoardSelectors.boardId);
  const title = useSelector(centralizedBoardTitleSelector);
  const { onNavigateToBoard } = usePublicBoard();

  const onBoardClick = useCallback(
    (locationBoard: LocationBoard) => {
      onNavigateToBoard({ boardId: locationBoard.id, boardTitle: locationBoard.title });
    },
    [onNavigateToBoard],
  );

  const breadcrumbItems = useMemo(() => {
    const items: BreadcrumbsItemType[] = [];

    ancestors?.map((ancestor) =>
      items.push({
        id: ancestor.id,
        label: ancestor.title,
        className: 'max-w-[170px]',
        onClick: () => onBoardClick({ id: ancestor.id, title: ancestor.title }),
        type: 'button',
      }),
    );

    if (id && title) {
      items.push({
        id,
        className: 'max-w-[350px]',
        label: title,
        type: 'item',
      });
    }

    return items;
  }, [ancestors, id, onBoardClick, title]);

  if (breadcrumbItems.length < 2) {
    return null;
  }

  return (
    <Breadcrumbs
      visibleItemsAtEnd={2}
      className="-ml-1"
      data-testid={BOARD_CRUMBS}
      isCompact={breadcrumbItems.length > COMPACT_BREADCRUMBS_THRESHOLD}
      items={breadcrumbItems}
    />
  );
});

PublicBoardHeaderAncestors.displayName = 'PublicBoardHeaderAncestors';
